import { Alert, Box, Link, Spinner, Tag, Text } from '@nimbus-ds/components';
import type { ReactElement } from 'react';
import { estadoCorrida } from '../formato';

export function Cargando({ texto = 'Cargando…' }: { readonly texto?: string }): ReactElement {
  return (
    <Box display="flex" flexDirection="column" alignItems="center" gap="2" padding="8">
      <Spinner size="large" />
      <Text fontSize="caption" color="neutral-textLow">
        {texto}
      </Text>
    </Box>
  );
}

export function ErrorCaja({
  titulo = 'Algo salió mal',
  mensaje,
  codigo = null,
  freno = false,
}: {
  readonly titulo?: string;
  readonly mensaje: string;
  /** Identificador de la ocurrencia. El mismo que quedó en nuestros logs. */
  readonly codigo?: string | null;
  /**
   * `true` cuando no se rompió nada: la app frenó a propósito.
   *
   * Cambia el color, el título y saca el código con el link a soporte. Un freno
   * deliberado mostrado como falla es un ticket de soporte que no tiene nada que
   * arreglar, y un comerciante convencido de que la app anda mal.
   */
  readonly freno?: boolean;
}): ReactElement {
  if (freno) {
    return (
      <Alert appearance="warning" title="Esperá un rato antes de seguir">
        <Text>{mensaje}</Text>
      </Alert>
    );
  }

  return (
    <Alert appearance="danger" title={titulo}>
      <Text>{mensaje}</Text>
      {codigo !== null && (
        <Text fontSize="caption" color="neutral-textLow">
          Código: {codigo} —{' '}
          <Link
            as="a"
            href={`/soporte#codigo=${encodeURIComponent(codigo)}`}
            target="_blank"
            rel="noopener noreferrer"
            fontSize="caption"
          >
            escribinos
          </Link>{' '}
          con ese código y encontramos qué pasó.
        </Text>
      )}
    </Alert>
  );
}

/** Colores del estado de una corrida, alineados con lo que significan. */
const APARIENCIA: Readonly<Record<string, 'neutral' | 'primary' | 'success' | 'warning' | 'danger'>> =
  {
    preview: 'neutral',
    queued: 'primary',
    running: 'primary',
    completed: 'success',
    completed_with_errors: 'warning',
    failed: 'danger',
    cancelled: 'neutral',
    undone: 'neutral',
  };

export function EtiquetaEstado({ estado }: { readonly estado: string }): ReactElement {
  return <Tag appearance={APARIENCIA[estado] ?? 'neutral'}>{estadoCorrida(estado)}</Tag>;
}
